import Vue from 'vue'
import Vuex from 'vuex'
import { cloneDeep, findIndex, fromPairs, pick, some } from 'lodash'
import constants from '@/helpers/constants'
import 'whatwg-fetch'
import 'abortcontroller-polyfill/dist/polyfill-patch-fetch'
import addDays from '@/helpers/addDays'
import toRegExp from '@/helpers/toRegExp'

Vue.use(Vuex)

const VALIDATION_DEFAULT_ERROR = 'update denied: validation failed'
const VALIDATION_START_DATE_RANGE_ERROR = 'update denied: date out of range'
const PLACE_OF_USE_REG_EX = toRegExp(process.env.VUE_APP_PLACE_OF_USE_REG_EX) || /^.+$/i

//https://stackoverflow.com/questions/42295340/how-to-clear-state-in-vuex-store
const getDefaultState = () => ({
    products: [],
    currentStep: 0,
    productUid: null,
    productPathSegment: null,
    productQuantity: null,
    startDate: null,
    endDate: null,
    placeOfUse: null,
    uidsOfSelectedProductOptions: [],
    pricesOfProductOptions: {},
    personalData: {},
    rentalPrice: null,
    rentalPriceRequestStatus: null,
    // determine text note to display next to price
    pricePeriod: null,
    // determine price cycle (per month/week/day etc.)
    priceFormat: null,
    transportPrice: null,
    transportPriceRequestStatus: null,
    checkoutStatus: null,
    checkoutErrorMessage: null
})
const defaultState = getDefaultState()
let abortController

export default new Vuex.Store({
    state: getDefaultState,
    getters: {
        product: (state, getters) => {
            return getters.productByUid(state.productUid)
        },
        productMapped: (state, getters) => {
            return getters.productByUidMapped(state.productUid)
        },
        productByUid: state => uid => {
            return cloneDeep(state.products.find(product => product.uid === uid))
        },
        productByUidMappedSubset: (state, getters) => uid => {
            const product = getters.productByUid(uid)
            if (!product) return
            return {
            }
        },
        productByUidMapped: (state, getters) => uid => {
            const product = getters.productByUid(uid)
            if (!product) return
            // deep clone already provided by product getter
            return Object.assign(product, getters.productByUidMappedSubset(uid))
        },
        selectedProductOptions: (state, getters) => {
            let options = getters.product.options.filter(option => state.uidsOfSelectedProductOptions.indexOf(option.uid) !== -1)
            options = options.map((option) => Object.assign({}, option, {
                price: state.pricesOfProductOptions[option.uid]
            }))

            return options
        },
        totalPrice: state => {
            return state.rentalPrice + state.transportPrice
        }
    },
    mutations: {
        reset (state, prop) {
            // eslint-disable-next-line no-prototype-builtins
            if (prop !== undefined && defaultState.hasOwnProperty(prop)) {
                state[prop] = defaultState[prop]
            } else if (prop === undefined) {
                Object.assign(state, getDefaultState())
            }
        },
        setCurrentStep (state, step) {
            step = parseInt(step, 10)
            if (isNaN(step) || step < 0 || step > 4) throw new Error(VALIDATION_DEFAULT_ERROR)
            state.currentStep = step
        },
        addProduct (state, productData) {
            const idxOfExistingProduct = findIndex(state.products, (product) => product.uid === productData.uid)
            if (idxOfExistingProduct !== -1) {
                state.products.splice(idxOfExistingProduct, 1, productData)
            } else {
                state.products.push(productData)
            }
        },
        setProductUid (state, uid) {
            if (!state.products.some(product => product.uid === uid)) throw new Error('update denied: no product with given uid "' + uid + '"')
            state.productUid = uid
        },
        setProductPathSegment (state, pathSegment) {
            state.productPathSegment = pathSegment
        },
        setProductQuantity (state, quantity) {
            quantity = parseInt(quantity, 10)
            if (isNaN(quantity) || quantity < 1 || quantity > 99) throw new Error(VALIDATION_DEFAULT_ERROR)
            state.productQuantity = quantity
        },
        setStartDate (state, date) {
            if (Object.prototype.toString.call(date) !== '[object Date]' || !(date instanceof Date) || date.toString() === 'Invalid Date') throw new Error(VALIDATION_DEFAULT_ERROR)
            const today = new Date(new Date().toDateString())
            const minDate = addDays(today, 2)
            date = new Date(date.toDateString())
            if (date < minDate) throw new Error(VALIDATION_START_DATE_RANGE_ERROR + ` (${date})`)
            if (state.endDate !== null && date > state.endDate) state.endDate = null
            state.startDate = date
        },
        setEndDate (state, date) {
            if (Object.prototype.toString.call(date) !== '[object Date]' || !(date instanceof Date) || date.toString() === 'Invalid Date') throw new Error(VALIDATION_DEFAULT_ERROR)
            const minDate = state.startDate
            if (date < minDate) throw new Error(VALIDATION_START_DATE_RANGE_ERROR + ` (${date})`)
            state.endDate = date
        },
        setPlaceOfUse (state, placeOfUse) {
            placeOfUse = placeOfUse.trim()
            if (typeof placeOfUse !== 'string' || !PLACE_OF_USE_REG_EX.test(placeOfUse)) throw new Error(VALIDATION_DEFAULT_ERROR)
            state.placeOfUse = placeOfUse
        },
        replaceUidsOfSelectedProducts (state, ids) {
            state.uidsOfSelectedProductOptions = ids
        },
        setPricesOfProductOptions (state, payload) {
            state.pricesOfProductOptions = Object.assign({}, state.pricesOfProductOptions, payload)
        },
        setPersonalData (state, payload) {
            state.personalData = Object.assign({}, state.personalData, payload)
        },
        setRentalPrice (state, price) {
            price = parseFloat(price)
            if (isNaN(price)) throw new Error(VALIDATION_DEFAULT_ERROR)
            state.rentalPrice = price
        },
        setRentalPriceRequestStatus (state, status) {
            if (Object.values(constants.requestStatuses).indexOf(status) === -1) throw new Error(VALIDATION_DEFAULT_ERROR)
            state.rentalPriceRequestStatus = status
        },
        setPricePeriod (state, pricePeriod) {
            if (Object.values(constants.pricePeriods).indexOf(pricePeriod) === -1) throw new Error(VALIDATION_DEFAULT_ERROR)
            state.pricePeriod = pricePeriod
        },
        setPriceFormat (state, priceFormat) {
            if (Object.values(constants.priceFormats).indexOf(priceFormat) === -1) throw new Error(VALIDATION_DEFAULT_ERROR)
            state.priceFormat = priceFormat
        },
        setTransportPrice (state, price) {
            price = parseFloat(price)
            if (isNaN(price)) throw new Error(VALIDATION_DEFAULT_ERROR)
            state.transportPrice = price
        },
        setTransportPriceRequestStatus (state, status) {
            if (Object.values(constants.requestStatuses).indexOf(status) === -1) throw new Error(VALIDATION_DEFAULT_ERROR)
            state.transportPriceRequestStatus = status
        },
        setCheckoutStatus (state, status) {
            if (Object.values(constants.requestStatuses).indexOf(status) === -1) throw new Error(VALIDATION_DEFAULT_ERROR)
            state.checkoutStatus = status
        },
        setCheckoutErrorMessage (state, msg) {
            if (typeof msg !== 'string') throw new Error(VALIDATION_DEFAULT_ERROR)
            state.checkoutErrorMessage = msg
        }
    },
    actions: {
        setProduct: ({ state, commit, dispatch }, payload) => {
            return new Promise((resolve) => {
                if (state.productPathSegment === payload.productPathSegment) {
                    resolve()
                    return
                }

                if (process.env.NODE_ENV === 'development') {
                    const productData = {
                        uid: 'rental-test1',
                        name: 'Rental&nbsp;Dummy DEV',
                        pricePeriod: 'day',
                        price: 1180.64,
                        badges: [
                            {
                                sorting: 0,
                                url: 'https://still-shop-int1.c-2749.maxcluster.net/media/smile_productlabel/imagelabel/li-ion.png',
                                alt: 'Alt 1'
                            },
                            {
                                sorting: 1,
                                url: 'https://still-shop-int1.c-2749.maxcluster.net/media/smile_productlabel/imagelabel/li-ion.png',
                                alt: 'Alt 2'
                            }
                        ],
                        categories: ['Elektrostapler 2,5&nbsp;t'],
                        loadCapacity: '1500 kg',
                        liftHeight: '2000 mm',
                        maxSpeed: '200 km/h',
                        truckHeight: '',
                        forkLength: '',
                        options: [
                            {
                                uid: 'SMP',
                                name: 'STILL Miete Plus',
                                description: 'Deckt das Maschinenbruchrisiko ab.',
                                inclusive: false
                            },
                            {
                                uid: 'B0',
                                name: 'Blei-Säure-Batterie',
                                description: '',
                                inclusive: true
                            },
                            {
                                uid: 'B16',
                                name: 'Li-Ion Batterie',
                                description: '',
                                inclusive: false,
                                isLiIon: true
                            },
                            {
                                uid: 'B18',
                                name: 'Batteriewechselgestell',
                                description: '',
                                inclusive: false
                            },
                            {
                                uid: 'B5',
                                name: 'Wechselbatterie 48 V',
                                description: '',
                                inclusive: false
                            },
                            {
                                uid: 'A2',
                                name: 'Anbaugerät 1',
                                description: '',
                                inclusive: false
                            }
                        ],
                        description: 'Rental Dummy 1 description',
                        imageUrl: 'https://still-shop-int1.c-2749.maxcluster.net/media/catalog/product/S/T/STILL_Produktbilder_EXU_01.png',
                        dataSheetUrl: 'https://still.shop/123',
                        preselectedOptions: ['SMP', 'B0'],
                        bindingInquiryPossible: 1,
                        excessBurden: 1000
                    }

                    setTimeout(() => {
                        commit('addProduct', productData)
                        commit('setProductUid', productData.uid)
                        commit('setProductPathSegment', payload.productPathSegment)
                        commit('replaceUidsOfSelectedProducts', productData.options
                            .filter(option => productData.preselectedOptions.indexOf(option.uid) !== -1)
                            .map(option => option.uid))
                        commit('setCurrentStep', 0)
                        dispatch('updateRentalPrice')
                        dispatch('updateTransportPrice', { placeOfUse: null })
                        resolve()
                    }, 400)
                } else {
                    const url = `${process.env.VUE_APP_PRODUCT_URL}/${payload.productPathSegment}`

                    fetch(url, {
                        method: 'GET',
                        cache: 'no-cache',
                        headers: {
                            Accept: 'application/json'
                        }
                    })
                        .then((response) => {
                            if (!response.ok) throw new Error('HTTP error - status: ' + response.status)
                            return response.json()
                        })
                        .then((productData) => {
                            productData.badges = productData.badges.map(badge => pick(badge, ['sorting', 'url', 'alt']))
                            productData.bindingInquiryPossible = (() => {
                                const bindingInquiryPossible = parseInt(productData.bindingInquiryPossible, 10)
                                return bindingInquiryPossible === 1 || bindingInquiryPossible === 0 ? bindingInquiryPossible : 0
                            })()
                            productData.excessBurden = (() => {
                                const excessBurden = parseInt(productData.excessBurden, 10)
                                return !isNaN(excessBurden) ? excessBurden : 0
                            })()

                            commit('addProduct', productData)
                            commit('setProductUid', productData.uid)
                            commit('setProductPathSegment', payload.productPathSegment)
                            commit('replaceUidsOfSelectedProducts', productData.options
                                .filter(option => productData.preselectedOptions.indexOf(option.uid) !== -1)
                                .map(option => option.uid))
                            commit('setCurrentStep', 0)
                            dispatch('updateRentalPrice')
                            dispatch('updateTransportPrice', { placeOfUse: null })
                            resolve()
                        })
                }
            })
        },
        updateRentalPrice: ({ state, commit }) => {
            if (state.rentalPriceRequestStatus === constants.requestStatuses.pending) return
            if (state.productUid === null || state.startDate === null || state.endDate === null) {
                commit('reset', 'rentalPrice')
                commit('reset', 'pricePeriod')
                commit('reset', 'priceFormat')
                commit('reset', 'pricesOfProductOptions')
                return
            }

            if (process.env.NODE_ENV === 'development') {
                commit('setRentalPriceRequestStatus', constants.requestStatuses.pending)
                setTimeout(() => {
                    commit('setRentalPrice', 480)
                    commit('setPricePeriod', constants.pricePeriods.week)
                    commit('setPriceFormat', constants.priceFormats.default)
                    commit('setPricesOfProductOptions', {
                        SMP: 18,
                        B16: 200,
                        B18: 0,
                        B5: 166
                    })
                    commit('setRentalPriceRequestStatus', constants.requestStatuses.succeeded)
                }, 1000)
            } else {
                const formatDate = function (date) {
                    // BEWARE: cannot use 'formatToParts' due to IE 11 support
                    // and we cannot use the strings from format functions in IE 11 because of https://www.csgpro.com/blog/2016/08/a-bad-date-with-internet-explorer-11-trouble-with-new-unicode-characters-in-javascript-date-strings/
                    // ensure 2-digit format for day and month
                    const day = ('0' + date.getDate()).slice(-2)
                    const month = ('0' + (date.getMonth() + 1)).slice(-2)
                    // DD.MM.YYYY
                    return day + '.' + month + '.' + date.getFullYear()
                }
                const startDate = formatDate(state.startDate)
                const endDate = formatDate(state.endDate)
                const url = `${process.env.VUE_APP_RENTAL_PRICE_URL}?sku=${state.productUid}&startDate=${startDate}&endDate=${endDate}`

                commit('setRentalPriceRequestStatus', constants.requestStatuses.pending)

                fetch(url, {
                    method: 'GET',
                    cache: 'no-cache'
                })
                    .then((response) => {
                        if (!response.ok) {
                            commit('setRentalPriceRequestStatus', constants.requestStatuses.failed)
                            throw new Error('HTTP error - status: ' + response.status)
                        }
                        return response.json()
                    })
                    .then((json) => {
                        if (json.error) {
                            commit('setRentalPriceRequestStatus', constants.requestStatuses.failed)
                            throw new Error('Rentalprice: ' + json.error)
                        }
                        if (some(json.options, (option) => option.error)) {
                            commit('setRentalPriceRequestStatus', constants.requestStatuses.failed)
                            throw new Error('Rentalprice option (check request for details)')
                        }

                        // add SMP price (default insurance option)
                        json.options.push({
                            sku: 'SMP',
                            price: json.smp_price
                        })

                        const pricePeriod = (() => {
                            if (json.price_info === 'rental_price_week') return constants.pricePeriods.week
                            else if (json.price_info === 'rental_price_month') return constants.pricePeriods.month
                            else if (json.price_info === 'overmonth') return constants.pricePeriods.overmonth
                            else return constants.pricePeriods.default
                        })()
                        const priceFormat = (() => {
                            if (json.price_format === 'monthly') return constants.priceFormats.monthly
                            else return constants.priceFormats.default
                        })()

                        let prices = json.options.map((option) => [option.sku, option.price])
                        prices = fromPairs(prices)

                        commit('setRentalPrice', json.price)
                        commit('setPricePeriod', pricePeriod)
                        commit('setPriceFormat', priceFormat)
                        commit('setPricesOfProductOptions', prices)
                        commit('setRentalPriceRequestStatus', constants.requestStatuses.succeeded)
                    })
            }
        },
        updateTransportPrice: ({ state, commit }, payload) => {
            // BEWARE: abortController always undefined in dev mode
            if (state.transportPriceRequestStatus === constants.requestStatuses.pending && abortController) abortController.abort()
            if (state.productUid === null || payload.placeOfUse === null) {
                commit('reset', 'placeOfUse')
                commit('reset', 'transportPrice')
                commit('setTransportPriceRequestStatus', constants.requestStatuses.default)
                return
            }

            if (process.env.NODE_ENV === 'development') {
                // BEWARE: Abort handling intentionally not implemented
                commit('setTransportPriceRequestStatus', constants.requestStatuses.pending)
                setTimeout(() => {
                    commit('setPlaceOfUse', payload.placeOfUse)
                    commit('setTransportPrice', 120 * state.productQuantity)
                    commit('setTransportPriceRequestStatus', constants.requestStatuses.succeeded)
                }, 2000)
            } else {
                const url = `${process.env.VUE_APP_TRANSPORT_PRICE_URL}?sku=${state.productUid}&postCode=${payload.placeOfUse}&quantity=${state.productQuantity}`

                commit('setTransportPriceRequestStatus', constants.requestStatuses.pending)
                abortController = new AbortController()
                const signal = abortController.signal

                fetch(url, {
                    method: 'GET',
                    cache: 'no-cache',
                    signal
                })
                    .then((response) => {
                        if (!response.ok) {
                            commit('setTransportPriceRequestStatus', constants.requestStatuses.failed)
                            throw new Error('HTTP error - status: ' + response.status)
                        }
                        return response.json()
                    })
                    .then((json) => {
                        if (json.error) {
                            commit('setTransportPriceRequestStatus', constants.requestStatuses.failed)
                            throw new Error('Transportprice: ' + json.error)
                        }

                        const price = parseFloat(json.costs)
                        if (isNaN(price)) {
                            commit('setTransportPriceRequestStatus', constants.requestStatuses.failed)
                            throw new Error('Error: costs value malformed => ' + json.costs)
                        }
                        commit('setPlaceOfUse', payload.placeOfUse)
                        commit('setTransportPrice', price)
                        commit('setTransportPriceRequestStatus', constants.requestStatuses.succeeded)
                    })
                    .catch(err => {
                        if (err.name === 'AbortError') return
                        commit('setTransportPriceRequestStatus', constants.requestStatuses.failed)
                        throw new Error('Transportprice: ' + err)
                    })
            }
        },
        checkout: ({ state, commit }, payload) => {
            if (state.checkoutStatus === constants.requestStatuses.pending ||
                state.checkoutStatus === constants.requestStatuses.succeeded) return
            if (payload === undefined || Object.values(constants.checkout.inquiryTypes).indexOf(payload.inquiryType) === -1) return

            if (process.env.NODE_ENV === 'development') {
                commit('setCheckoutStatus', constants.requestStatuses.succeeded)
            } else {
                const url = process.env.VUE_APP_CHECKOUT_URL
                const formatDate = function (date) {
                    // BEWARE: cannot use 'formatToParts' due to IE 11 support
                    // and we cannot use the strings from format functions in IE 11 because of https://www.csgpro.com/blog/2016/08/a-bad-date-with-internet-explorer-11-trouble-with-new-unicode-characters-in-javascript-date-strings/
                    // ensure 2-digit format for day and month
                    const day = ('0' + date.getDate()).slice(-2)
                    const month = ('0' + (date.getMonth() + 1)).slice(-2)
                    // YYYY-MM-DD
                    return date.getFullYear() + '-' + month + '-' + day
                }
                const postData = {
                    sku: state.productUid,
                    productQuantity: state.productQuantity,
                    startDate: formatDate(state.startDate),
                    endDate: formatDate(state.endDate),
                    productOptionUids: state.uidsOfSelectedProductOptions,
                    personalData: state.personalData,
                    inquiryType: payload.inquiryType,
                    // CCM19 consent transfer to backend
                    ccm_embeddings: window.CCM ? window.CCM.acceptedEmbeddings.map((entry) => entry.id).join(',') : ''
                }
                if (state.placeOfUse) postData.postCode = state.placeOfUse

                commit('setCheckoutStatus', constants.requestStatuses.pending)

                fetch(url, {
                    method: 'POST',
                    cache: 'no-store',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(postData)
                })
                    .then((response) => {
                        if (!response.ok) {
                            commit('setCheckoutStatus', constants.requestStatuses.failed)
                            commit('setCheckoutErrorMessage', response.status)
                            throw new Error('HTTP error - status: ' + response.status)
                        }
                        return response.json()
                    })
                    .then((json) => {
                        if (!json) {
                            commit('setCheckoutStatus', constants.requestStatuses.failed)
                        } else {
                            commit('setCheckoutStatus', constants.requestStatuses.succeeded)
                        }
                    })
            }
        }
    },
    modules: {}
})
